import {
  type OpenChangeReason,
  autoUpdate,
  flip,
  hide,
  offset,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
  useRole,
  useTransitionStatus,
} from '@floating-ui/react';
import { interactionResponse } from '@zillow/yield-callback';
import {
  type SyntheticEvent,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';

import { useReducedMotion } from '../../../hooks/use-reduced-motion';
import { useViewportLeaveClose } from '../../../hooks/use-viewport-leave-close';
import { OVERLAY_TRANSITION_DURATION_MS } from '../../../utils/motion';
import { CALENDAR_CHANGE_REASON } from '../../calendar/constants/calendar-change-reason';
import type { DatePickerRootPropsInterface } from '../types/date-picker-root.types';
import type {
  DatePickerFloatingContextInterface,
  UseDatePickerReturnInterface,
} from '../types/use-date-picker.types';

export function useDatePicker(props: Partial<DatePickerRootPropsInterface>): {
  datePicker: UseDatePickerReturnInterface;
  floating: DatePickerFloatingContextInterface;
} {
  const {
    closeModalOnDateSelect = false,
    defaultOpen,
    defaultValue,
    offset: offsetValue = 8,
    onOpenChange: setControlledOpen,
    onValueChange: setControlledValue,
    open: controlledOpen,
    overflowPadding = 8,
    placement = 'bottom-start',
    shouldAwaitInteractionResponse = true,
    shouldCloseOnViewportLeave = false,
    useClickProps,
    useDismissProps,
    useRoleProps,
    value: controlledValue,
  } = props;

  /**
   * Tracks whether the current open came from activating the trigger so focus can remain on it,
   * matching the `Dropdown` and `Menu` disclosure pattern. `useClick` reports reason 'click' for
   * both pointer and keyboard (Enter/Space) activation, so this is true for either input method.
   * Named for parity with `useDropdown` and `useMenu`.
   */
  const openedByPointerRef = useRef<boolean>(false);

  const contentId = useId();
  const [labelId, setLabelId] = useState<string | undefined>();
  const [descriptionId, setDescriptionId] = useState<string | undefined>();

  const [pendingValue, setPendingValue] = useState<string | Date | undefined>(controlledValue);

  const resetPendingValue = useCallback(() => {
    setPendingValue(controlledValue ?? '');
  }, [controlledValue]);

  const [uncontrolledValue, setUncontrolledValue] = useState<string | Date | undefined>(
    defaultValue,
  );

  const isControlledValue = 'value' in props;
  const value = isControlledValue ? controlledValue : uncontrolledValue || '';

  /**
   * Open state management (controlled or uncontrolled)
   */
  const [uncontrolledOpen, setUncontrolledOpen] = useState(defaultOpen);
  const isControlledOpen = controlledOpen !== undefined;
  const open = isControlledOpen ? controlledOpen : uncontrolledOpen || false;

  /*
   * Closes that bypass Floating UI's `onOpenChange` (controlled `open`, `DatePicker.Close`) leave
   * the ref stale, so reopening from anywhere but the trigger would wrongly keep focus on it.
   */
  useEffect(() => {
    if (!open) {
      openedByPointerRef.current = false;
    }
  }, [open]);

  /**
   * Floating UI setup for modal state management
   */
  const { context, refs, floatingStyles, middlewareData, isPositioned } = useFloating({
    placement,

    open,

    onOpenChange: async (state, event, reason) => {
      openedByPointerRef.current = state && reason === 'click';

      if (shouldAwaitInteractionResponse) {
        await interactionResponse();
      }

      if (reason === 'escape-key') {
        resetPendingValue?.();
        onValueChange?.(value ?? '', { reason: CALENDAR_CHANGE_REASON.escKey });
      }

      updateOpenState(state, event, reason);
    },

    whileElementsMounted: autoUpdate,

    /**
     * Middleware plugins have to be in certain order.
     * See: https://floating-ui.com/docs/useFloating#middleware
     */
    middleware: [
      // Translates the floating element along the specified axes
      offset(offsetValue),

      // Changes the placement of the floating element to keep it in view
      flip({
        fallbackPlacements: ['top-start', 'top-end', 'bottom-start', 'bottom-end'],
        padding: overflowPadding,
      }),

      // A data provider that allows you to hide the floating element
      ...(shouldCloseOnViewportLeave ? [hide({ padding: overflowPadding })] : []),
    ],
  });

  /**
   * Floating UI interaction hooks for accessibility and behavior
   * Sets up click and dismiss event handlers, and ARIA attributes based on role
   */
  const click = useClick(context, {
    enabled: true,
    event: 'mousedown',
    ...useClickProps,
  });

  const dismiss = useDismiss(context, {
    enabled: true,
    outsidePress: (event) =>
      !(event.target as HTMLElement).closest('[data-c11n-component="DatePicker.Root"]'),
    ...useDismissProps,
  });

  const role = useRole(context, {
    role: 'dialog',
    ...useRoleProps,
  });

  const { getFloatingProps, getReferenceProps } = useInteractions([click, dismiss, role]);

  /**
   * Motion helpers and styles
   */
  const reducedMotion = useReducedMotion();

  const { isMounted, status: transitionStatus } = useTransitionStatus(context, {
    duration: reducedMotion ? 0 : OVERLAY_TRANSITION_DURATION_MS,
  });

  /**
   * Handles controlled or uncontrolled "open" state.
   *
   * @param newValue - New state
   * @param event - Event object
   * @param reason - Reason for state change
   */
  const updateOpenState = useCallback(
    (newValue: boolean, event?: Event, reason?: OpenChangeReason) => {
      if (isControlledOpen) {
        setControlledOpen?.(newValue, event, reason);
      } else {
        setUncontrolledOpen(newValue);
        setControlledOpen?.(newValue, event, reason);
      }
    },
    [isControlledOpen, setControlledOpen],
  );

  useViewportLeaveClose({
    enabled: shouldCloseOnViewportLeave,
    isPositioned,
    middlewareData,
    onOpenChange: updateOpenState,
    open,
  });

  /**
   * Handles `DatePicker.Close` click actions.
   *
   * @param event - Event object
   */
  const onClickClose = useCallback(
    async (event: SyntheticEvent) => {
      if (shouldAwaitInteractionResponse) {
        await interactionResponse();
      }

      updateOpenState(false, event as unknown as Event, 'click');
    },
    [updateOpenState, shouldAwaitInteractionResponse],
  );

  /**
   * Handles programmatic close action.
   */
  const onClose = useCallback(() => {
    updateOpenState(false);
  }, [updateOpenState]);

  const onValueChange = useCallback(
    (date: Date | string, meta: { reason: string }) => {
      setPendingValue(date);

      // for manual input, update the actual value immediately
      if (meta.reason === CALENDAR_CHANGE_REASON.manualInput) {
        if (isControlledValue) {
          setControlledValue?.(date, meta);
        } else {
          setUncontrolledValue(date);
          setControlledValue?.(date, meta);
        }
        return;
      }

      // For calendar selections, follow the closeModalOnDateSelect behavior
      if (
        meta.reason !== CALENDAR_CHANGE_REASON.ok &&
        meta.reason !== CALENDAR_CHANGE_REASON.cancel &&
        !closeModalOnDateSelect
      ) {
        return;
      }

      if (isControlledValue) {
        setControlledValue?.(date, meta);
      } else {
        setUncontrolledValue(date);
        setControlledValue?.(date, meta);
      }

      if (
        closeModalOnDateSelect ||
        meta.reason === CALENDAR_CHANGE_REASON.ok ||
        meta.reason === CALENDAR_CHANGE_REASON.cancel
      ) {
        onClose();
      }
    },
    [isControlledValue, closeModalOnDateSelect, setControlledValue, onClose],
  );

  const datePicker = useMemo(
    () => ({
      // Internal focus management
      openedByPointerRef,

      // Open/mount state and close handlers
      closeModalOnDateSelect,
      open,
      isMounted,
      onClickClose,
      onClose,

      // Value props and handlers
      pendingValue,
      setPendingValue,
      resetPendingValue,
      onValueChange,
      value,

      // Accessibility props and handlers
      contentId,
      labelId,
      setLabelId,
      descriptionId,
      setDescriptionId,

      // Refs
      refs,

      // Trigger and reference props
      getReferenceProps,

      // Styles
      floatingStyles,
      transitionStatus,

      middlewareData,
    }),
    [
      openedByPointerRef,
      closeModalOnDateSelect,
      open,
      isMounted,
      onClickClose,
      onClose,
      pendingValue,
      setPendingValue,
      resetPendingValue,
      onValueChange,
      value,
      contentId,
      labelId,
      descriptionId,
      refs,
      getReferenceProps,
      floatingStyles,
      transitionStatus,
      middlewareData,
    ],
  );

  /*
   * Floating-ui-bridged data. Keep it on a separate context so only
   * DatePicker.Content pays for fresh Floating UI values.
   */
  const floating = { context, getFloatingProps };

  return { datePicker, floating };
}
